import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Pause, Target, Heart, TrendingUp, Users, Sparkles } from 'lucide-react';

const IntroSection = () => {
  const [ref, inView] = useInView({
    threshold: 0,
    triggerOnce: true,
    rootMargin: '50px 0px', // Start animation when component is 50px before entering viewport
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const benefits = [
    {
      icon: <TrendingUp size={24} />,
      title: "העלאת מוטיבציה",
      description: "עובדים מרגישים יותר מקושרים למטרות שלהם",
      color: "from-dream-teal to-dream-warm-brown"
    },
    {
      icon: <Target size={24} />,
      title: "בהירות פנימית",
      description: "חדות ומיקוד טוב יותר במטרות הארגון",
      color: "from-dream-warm-brown to-dream-teal"
    },
    {
      icon: <Heart size={24} />,
      title: "תחושת שייכות",
      description: "העמקת הקשר והמשמעות בעבודה",
      color: "from-dream-warm-brown to-dream-pink-beige"
    }
  ];

  return (
    <section className="section-padding bg-gradient-to-br from-dream-soft-beige via-dream-pink-beige to-dream-light-beige relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-40 h-40 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full blur-3xl opacity-30"></div>
        <div className="absolute bottom-20 left-20 w-32 h-32 bg-gradient-to-r from-dream-pink-beige to-dream-soft-beige rounded-full blur-2xl opacity-30"></div>
      </div>

      <motion.div
        ref={ref}
        className="container-width relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <motion.div variants={itemVariants} className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full flex items-center justify-center">
              <Pause className="text-white" size={24} />
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            עוצרים רגע בין <span className="gradient-text">המשימות</span>
          </h2>
        </motion.div>

        <motion.div variants={itemVariants} className="max-w-5xl mx-auto mb-16">
          <div className="bg-white p-8 md:p-12 rounded-3xl shadow-lg">
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6 text-center">
              בין משימות, יעדים ולו"ז צפוף – אנחנו עוצרים רגע.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              בסדנת לוח חזון בארגונים ניצור יחד מרחב נשימה שבו כל משתתפת ומשתתף יוצאים למסע אישי.
              באמצעות עבודה עם אוזניות, דמיון מודרך, כלים יצירתיים ולוח חזון מוחשי – 
              נזמין כל אחד ואחת להתחבר למה שמניע אותם באמת, לחלומות, לתשוקות ולמטרות 
              שראויות לקבל מקום גם בתוך הארגון.
            </p>
            
            {/* Vision board creation image */}
            <div className="my-8">
              <div className="relative w-full max-w-md mx-auto">
                <img 
                  src="/images/WhatsApp Image 2025-08-11 at 10.16.20 (3).jpeg"
                  alt="יצירת לוח חזון"
                  className="w-full h-56 object-cover rounded-2xl shadow-lg"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-dream-soft-beige to-dream-pink-beige p-6 rounded-2xl">
              <p className="text-lg text-gray-700 leading-relaxed font-semibold text-center">
                הסדנה נוגעת ברובד האישי והרגשי – אך התוצאות מורגשות גם בשטח
              </p>
            </div>
          </div>
        </motion.div>

        {/* Benefits */}
        <motion.div variants={itemVariants} className="mb-16">
          <h3 className="text-2xl font-bold text-center text-gray-800 mb-8">
            התוצאות <span className="gradient-text">המורגשות</span>
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-center"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${benefit.color} rounded-full flex items-center justify-center mx-auto mb-4 text-white`}>
                  {benefit.icon}
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h4>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Unique Approach */}
        <motion.div variants={itemVariants} className="text-center">
          <div className="bg-gradient-to-br from-dream-soft-beige to-dream-pink-beige p-8 rounded-3xl">
            <div className="flex justify-center mb-6">
              <div className="flex gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full flex items-center justify-center">
                  <Users className="text-white" size={20} />
                </div>
                <div className="w-12 h-12 bg-gradient-to-r from-dream-warm-brown to-dream-pink-beige rounded-full flex items-center justify-center">
                  <Heart className="text-white" size={20} />
                </div>
                <div className="w-12 h-12 bg-gradient-to-r from-dream-warm-brown to-dream-teal rounded-full flex items-center justify-center">
                  <Sparkles className="text-white" size={20} />
                </div>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">מרחב ייחודי</h3>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
              מרחב שהוא גם אישי וגם ארגוני. גם רך וגם מדויק. גם עוצר – וגם מניע קדימה.
              הסדנה יוצרת איזון מושלם בין צרכי הפרט לצרכי הארגון ומאפשרת צמיחה משמעותית בשני הרבדים.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default IntroSection;
